import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  Badge,
  Banner,
  Button,
  SelectionList,
  SelectionListItem,
} from '@ths/ui';

import { HotelSavedCardStatus } from 'generated/graphql';

import { HotelCardSetupModal } from '../../hotel/HotelCardSetupModal';
import { SavedCardsSkeleton } from '../../hotel/SavedCardsSkeleton';
import { useHotelCardSetup } from '../../hotel/useHotelCardSetup';
import { HotelSavedCardSelection } from '../../hotel/useHotelSavedCardSelection';

const labels: Record<HotelSavedCardStatus, string> = {
  AVAILABLE: 'Available',
  EXPIRED: 'Expired',
  REMOVED: 'Removed',
  UNKNOWN: 'Status unavailable',
};
export function SavedCardSelection({
  selection,
  locked,
  identity,
}: {
  selection: HotelSavedCardSelection;
  locked: boolean;
  identity: string;
}) {
  const setup = useHotelCardSetup(
    selection.required === true ? identity : '',
    selection.refresh,
  );
  if (selection.required === false) return null;
  const initialLoading =
    selection.required == null ||
    (selection.loading && !selection.methods.length);
  return (
    <section
      className="space-y-4 rounded-md border border-separator bg-surface p-5 text-text"
      aria-label="Payment Method"
      aria-busy={selection.loading}
    >
      <h2 className="text-base font-medium">Payment Method</h2>
      {selection.error ? (
        <Banner status="error">
          Unable to check saved cards. Reload this page and try again.
        </Banner>
      ) : initialLoading ? (
        <SavedCardsSkeleton withAction />
      ) : !selection.configured ? (
        <Banner status="warning">
          Card setup is currently unavailable. Contact THS for assistance.
        </Banner>
      ) : selection.methods.length === 0 ? (
        <p className="text-text-secondary">You have no saved cards.</p>
      ) : (
        <SelectionList aria-label="Saved cards">
          {selection.methods.map((method) => {
            const selected =
              selection.selectedId === method.id &&
              method.status === HotelSavedCardStatus.Available;
            return (
              <SelectionListItem
                key={method.id}
                truncate={false}
                title={`${method.brand} ending in ${method.last4}`}
                secondary={`Expires ${String(method.expiryMonth).padStart(2, '0')}/${method.expiryYear} · ${labels[method.status]}`}
                selected={selected}
                aria-pressed={selected}
                className={
                  selected
                    ? 'bg-accent-wash ring-2 ring-inset ring-accent'
                    : undefined
                }
                leading={
                  selected ? (
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="size-5 shrink-0 text-accent"
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="size-5 shrink-0 rounded-full border border-separator"
                    />
                  )
                }
                trailing={
                  <span className="flex flex-col items-end gap-1">
                    {method.isDefault && (
                      <Badge status="neutral">Default</Badge>
                    )}
                    {selected && (
                      <span className="text-label font-medium text-accent">
                        Selected
                      </span>
                    )}
                  </span>
                }
                disabled={
                  locked ||
                  selection.loading ||
                  !!setup.state ||
                  method.status !== HotelSavedCardStatus.Available
                }
                onClick={() => selection.select(method.id)}
              />
            );
          })}
        </SelectionList>
      )}
      {!initialLoading && (
        <div className="flex flex-wrap gap-2">
          <Button
            ref={setup.returnFocus}
            type="button"
            onClick={() => void setup.addCard()}
            disabled={
              locked ||
              selection.loading ||
              !selection.configured ||
              !!selection.error
            }
            loading={setup.loading}
          >
            Add a card
          </Button>
        </div>
      )}
      <HotelCardSetupModal setup={setup} />
      {!selection.ready && !selection.loading && (
        <p className="text-sm text-text-secondary">
          Select an available card to continue.
        </p>
      )}
    </section>
  );
}
